import { ReactNode } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import { Link, generatePath } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { PermissionLevelEnum } from "@models/PermissionSection";
import FoodSidebarLock from "@pages/Food/refresh/components/FoodSidebarLock/FoodSidebarLock";
import { IoFastFoodOutline } from "react-icons/io5";
import { IFoodProfileSummary } from "@models/NewFoodProfileSummary";
import { routeNames } from "@core/routeNames";
import { sanitizeUrl } from "@helpers";
import useFoodProfileRelatedContent, {
    FoodProfileRelatedLink,
    FoodProfileRelatedLinks,
    FoodProfileRelatedProfile,
} from "@apiHooks/useFoodProfileRelatedContent";
import ProfileSidebar from "@components/ProfileSidebar/ProfileSidebar";
import ProfileSidebarSection from "@components/ProfileSidebarSection/ProfileSidebarSection";
import FoodProfileSidebarActions from "@pages/Food/refresh/components/FoodProfileSidebarActions/FoodProfileSidebarActions";
import "./FoodProfileSidebar.scss";

interface Props {
    summary: IFoodProfileSummary;
    isCollapsed: boolean;
}

interface SectionItem {
    id: number;
    link: ReactNode;
}

// Related content opens in a new tab so the user keeps their place on this profile (PO decision).
const NEW_TAB = { target: "_blank", rel: "noopener noreferrer" } as const;

const toCategoryProfileItem = ({ id, name }: FoodProfileRelatedProfile): SectionItem => ({
    id,
    link: (
        <Link className="FoodProfileSidebar__link" to={generatePath(routeNames.categoryProfile, { id })} {...NEW_TAB}>
            {name}
        </Link>
    ),
});

const toFoodProfileItem = ({ id, name }: FoodProfileRelatedProfile): SectionItem => ({
    id,
    link: (
        <Link
            className="FoodProfileSidebar__link"
            to={{ pathname: routeNames.food, search: `?q=${encodeURIComponent(name)}` }}
            {...NEW_TAB}
        >
            {name}
        </Link>
    ),
});

type LinkedItem = FoodProfileRelatedLink & { link: string };
const hasLink = (item: FoodProfileRelatedLink): item is LinkedItem => !!item.link;

// The BE returns complete URLs for instant charts and MACs; `sanitizeUrl` normalises them like other BE links.
const toApiLinkItem = ({ id, name, link }: LinkedItem): SectionItem => ({
    id,
    link: <a className="FoodProfileSidebar__link" href={sanitizeUrl(link)} {...NEW_TAB}>{name}</a>,
});

// Locked items stay visible (so the user sees what exists) but are not navigable.
const toLockedItem = ({ id, name }: FoodProfileRelatedLink): SectionItem => ({
    id,
    link: <span className="FoodProfileSidebar__link is-disabled" aria-disabled="true">{name}</span>,
});

interface LockConfig {
    label: string;
    message: string;
    feature: string;
}

const INSTANT_CHART_LOCK: LockConfig = {
    label: "Instant Charts are locked",
    message: "Upgrade your subscription to see Instant Charts.",
    feature: "mtd",
};

const MAC_LOCK: LockConfig = {
    label: "MACs are locked",
    message: "Upgrade your subscription to see MACs.",
    feature: "macs",
};

const FoodProfileSidebar: FCX<Props> = ({ summary, isCollapsed }) => {
    const {
        data: { imageUrl, categoryProfiles, foodProfiles, instantCharts, macs },
        isLoading,
    } = useFoodProfileRelatedContent(summary.name);
    const dispatch = useDispatch();

    const requestTrial = (feature: string) => {
        toast.info("Request has been sent");
        dispatch(requestTrialByFeature(feature));
    };

    // The BE permission wrapper decides the lock: anything below Full access is locked, like other permission sections.
    const lockFor = ({ permissionLevel }: FoodProfileRelatedLinks, lock: LockConfig) =>
        permissionLevel === PermissionLevelEnum.Full ? undefined : lock;

    // Locked: every item is listed (the BE sends no URL without access). With access an item needs a URL to link to.
    const apiLinkItems = ({ items }: FoodProfileRelatedLinks, lock?: LockConfig) =>
        lock ? items.map(toLockedItem) : items.filter(hasLink).map(toApiLinkItem);

    const instantChartLock = lockFor(instantCharts, INSTANT_CHART_LOCK);
    const macLock = lockFor(macs, MAC_LOCK);

    const sections = [
        { title: "Category", testId: "blockFoodSidebarCategories", items: categoryProfiles.map(toCategoryProfileItem) },
        { title: "Profile", testId: "blockFoodSidebarProfiles", items: foodProfiles.map(toFoodProfileItem) },
        {
            title: "Instant Chart",
            testId: "blockFoodSidebarInstantCharts",
            items: apiLinkItems(instantCharts, instantChartLock),
            lock: instantChartLock,
        },
        { title: "MAC", testId: "blockFoodSidebarMacs", items: apiLinkItems(macs, macLock), lock: macLock },
    ].filter(({ items }) => items.length > 0);

    // Always keep the image frame: the placeholder shows while loading, when there is no image and when it fails.
    const image = { src: imageUrl ?? "", alt: summary.name, placeholder: <IoFastFoodOutline/> };

    return (
        <ProfileSidebar
            className="FoodProfileSidebar"
            testId="blockFoodProfileSidebar"
            imageTestId="imgFoodProfileSidebar"
            // No title here: the profile name sits in the sticky sections nav above the sidebar (FoodRefresh).
            image={image}
            actions={<FoodProfileSidebarActions summary={summary} isCollapsed={isCollapsed}/>}
            isCollapsed={isCollapsed}
            contentTitle={sections.length > 0 && "Related Content"}
        >
            {sections.map(({ title, testId, items, lock }) => (
                <ProfileSidebarSection
                    key={title}
                    className="FoodProfileSidebar__section"
                    title={lock ? (
                        <>
                            {title}
                            <FoodSidebarLock
                                label={lock.label}
                                message={lock.message}
                                onContactUsClick={() => requestTrial(lock.feature)}
                                testId={`${testId}Lock`}
                            />
                        </>
                    ) : title}
                    testId={testId}
                >
                    <ul className="FoodProfileSidebar__list">
                        {items.map(({ id, link }) => <li key={id}>{link}</li>)}
                    </ul>
                </ProfileSidebarSection>
            ))}
        </ProfileSidebar>
    );
};

export default FoodProfileSidebar;
